import React from 'react';

import FilterChip from '../ui/FilterChip';
import DocumentCard from '../ui/DocumentCard';

/**
 * ContlyRightSidebarDesktop Component
 * 
 * Desktop version of the Contly right sidebar.
 * Exact copy of original ContlyRightSidebar with max-lg:hidden removed.
 */
const ContlyRightSidebarDesktop = ({
  hidden = false,
  documents = [],
  activeFilter = 'all',
  onFilterChange,
  onDocumentClick,
  onAddDocument,
  searchQuery = '',
  theme = 'dark',
  onDeleteDocument,
}) => {
  const docs = documents.map(d => ({
    ...d,
    title: d.title || d.name,
    filename: d.filename || d.name,
    badge: d.badge || 'Document',
    skeleton: false
  }));

  // Filter documents based on active filter
  const filteredDocs = activeFilter === 'all'
    ? docs
    : docs.filter(doc => doc.type === activeFilter);

  // Filter by search query
  const searchFilteredDocs = searchQuery.trim()
    ? filteredDocs.filter(doc =>
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.filename.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : filteredDocs;

  // Badge style configurations
  const badgeStyles = {
    review: {
      color: '#FF9F43',
      border: 'rgba(255, 159, 67, 0.3)',
      background: 'rgba(255, 159, 67, 0.1)',
      shadow: '0 0 8px rgba(255, 159, 67, 0.2)',
    },
    draft: {
      color: '#FDCB6E',
      border: 'rgba(253, 203, 110, 0.3)',
      background: 'rgba(253, 203, 110, 0.1)',
      shadow: '0 0 8px rgba(253, 203, 110, 0.2)',
    },
    signed: {
      color: '#55EFC4',
      border: 'rgba(85, 239, 196, 0.3)',
      background: 'rgba(85, 239, 196, 0.1)',
      shadow: '0 0 8px rgba(85, 239, 196, 0.2)',
    },
    ready: {
      color: '#a855f7',
      border: 'rgba(168, 85, 247, 0.3)',
      background: 'rgba(168, 85, 247, 0.1)',
      shadow: '0 0 8px rgba(168, 85, 247, 0.2)',
    },
  };

  if (hidden) {
    return null;
  }

  return (
    <aside
      id="contlyRightSidebar"
      className="right-sidebar fixed right-0 top-0 w-[340px] h-screen flex-col overflow-hidden z-[50]"
      style={{
        background: theme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
        WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
        borderLeft: theme === 'dark'
          ? '0.5px solid rgba(255, 255, 255, 0.08)'
          : '0.5px solid rgba(71, 85, 105, 0.4)',
        boxShadow: theme === 'light'
          ? '0 0 4px rgba(15, 23, 42, 0.08), inset -1px 0 2px rgba(0, 0, 0, 0.02)'
          : 'none',
        display: 'flex',
      }}
      data-theme={theme}
    >
      {/* Right Sidebar Header */}
      <div
        className="right-sidebar-header"
        style={{
          padding: 'calc(60px + 6px) 12px 12px',
          borderBottom: theme === 'dark'
            ? '0.5px solid rgba(255, 255, 255, 0.08)'
            : '0.5px solid rgba(71, 85, 105, 0.4)',
          boxShadow: theme === 'light'
            ? '0 2px 4px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)'
            : 'none',
        }}
      >
        <h2
          className="m-0 mb-1 text-[20px] font-semibold"
          style={{
            color: theme === 'dark' ? '#ffffff' : '#0f172a',
          }}
        >
          Documents
        </h2>
        <p
          className="m-0 mb-3 text-[12px]"
          style={{
            color: theme === 'dark' ? '#888888' : '#1e293b',
          }}
        >
          Manage and search your files
        </p>

        {/* Search Input */}
        <input
          type="search"
          className="doc-search w-full h-[42px] px-3 rounded-2xl border outline-none text-[13px] font-inherit mb-4 transition-all duration-300"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => onSearchChange?.(e.target.value)}
          style={{
            border: theme === 'dark'
              ? '0.5px solid rgba(255, 255, 255, 0.2)'
              : '0.5px solid rgba(71, 85, 105, 0.5)',
            background: theme === 'dark'
              ? 'rgba(255, 255, 255, 0.12)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
            WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
            color: theme === 'dark' ? '#ffffff' : '#0f172a',
            boxShadow: theme === 'dark'
              ? 'inset 0 2px 4px rgba(0, 0, 0, 0.4)'
              : '0 1px 3px rgba(15, 23, 42, 0.08), inset 0 1px 2px rgba(0, 0, 0, 0.02)',
          }}
          onFocus={(e) => {
            if (theme === 'dark') {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1)';
            } else {
              e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.6)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.98)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08), inset 0 1px 2px rgba(0, 0, 0, 0.02)';
            }
          }}
          onBlur={(e) => {
            if (theme === 'dark') {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
              e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.4)';
            } else {
              e.currentTarget.style.borderColor = 'rgba(71, 85, 105, 0.5)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(15, 23, 42, 0.08), inset 0 1px 2px rgba(0, 0, 0, 0.02)';
            }
          }}
        />

        {/* Filter Chips */}
        <div className="filter-chips flex gap-1.5 flex-wrap">
          {['all', 'contracts', 'templates'].map((filter) => (
            <FilterChip
              key={filter}
              label={filter.charAt(0).toUpperCase() + filter.slice(1)}
              isActive={activeFilter === filter}
              onClick={() => onFilterChange?.(filter)}
              theme={theme}
            />
          ))}
        </div>
      </div>

      {/* Document List */}
      <ul
        id="docList"
        className="doc-list flex-1 overflow-y-auto p-3 flex flex-col gap-1.5"
      >
        {searchFilteredDocs.map((doc) => (
          <li key={doc.id}>
            <DocumentCard
              document={doc}
              onClick={onDocumentClick}
              theme={theme}
              showSkeleton={doc.skeleton !== false}
              badgeStyleMap={badgeStyles}
              badgeLabelMap={(badge) => {
                if (badge === 'review') return 'Review Needed';
                if (badge === 'draft') return 'Draft';
                if (badge === 'signed') return 'Signed';
                if (badge === 'ready') return 'Ready';
                return badge;
              }}
              onDelete={(doc) => onDeleteDocument?.(doc.id)}
            />
          </li>
        ))}
      </ul>

      {/* Right Sidebar Footer */}
      <div
        className="right-sidebar-footer p-3"
        style={{
          borderTop: theme === 'dark'
            ? '0.5px solid rgba(255, 255, 255, 0.08)'
            : '0.5px solid rgba(71, 85, 105, 0.4)',
          boxShadow: theme === 'light'
            ? '0 -2px 4px rgba(15, 23, 42, 0.06), 0 -1px 2px rgba(0, 0, 0, 0.04)'
            : 'none',
        }}
      >
        <div
          className="add-doc-area border-2 border-dashed rounded-2xl p-5 flex items-center justify-center cursor-pointer transition-all duration-300 bg-transparent active:scale-[0.98]"
          onClick={onAddDocument}
          style={{
            borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(71, 85, 105, 0.4)',
            boxShadow: theme === 'light'
              ? '0 1px 3px rgba(15, 23, 42, 0.08), 0 0 1px rgba(0, 0, 0, 0.05)'
              : 'none',
          }}
          onMouseEnter={(e) => {
            if (theme === 'dark') {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
            } else {
              e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.6)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(71, 85, 105, 0.4)';
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.boxShadow = theme === 'light'
              ? '0 1px 3px rgba(15, 23, 42, 0.08), 0 0 1px rgba(0, 0, 0, 0.05)'
              : 'none';
          }}
        >
          <button
            className="add-doc-btn w-14 h-14 rounded-full border flex items-center justify-center transition-all duration-300 active:scale-[0.98]"
            onClick={(e) => {
              e.stopPropagation();
              onAddDocument?.();
            }}
            style={{
              background: theme === 'dark'
                ? 'linear-gradient(135deg, rgba(40, 40, 40, 0.9), rgba(20, 20, 20, 0.95))'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: theme === 'light' ? 'blur(20px)' : 'none',
              WebkitBackdropFilter: theme === 'light' ? 'blur(20px)' : 'none',
              border: theme === 'dark'
                ? '0.5px solid rgba(255, 255, 255, 0.1)'
                : '0.5px solid rgba(71, 85, 105, 0.5)',
              color: theme === 'dark' ? '#ffffff' : '#0f172a',
              boxShadow: theme === 'light'
                ? '0 2px 4px rgba(15, 23, 42, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05)'
                : 'none',
            }}
            aria-label="Add document"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2V26M2 14H26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default ContlyRightSidebarDesktop;

